/**
 * Accessibility Module
 * Implements keyboard navigation, ARIA labels, and accessibility enhancements
 */

/**
 * Initialize accessibility features
 */
export function initializeAccessibility() {
    initializeKeyboardNavigation();
    addMissingAriaLabels();
    checkColorContrast();
}

/**
 * Initialize keyboard navigation handlers
 */
function initializeKeyboardNavigation() {
    document.addEventListener('keydown', handleKeyboardEvent);
}

/**
 * Handle keyboard events for accessibility
 * @param {KeyboardEvent} e - Keyboard event
 */
function handleKeyboardEvent(e) {
    // Focus management for modals
    if (e.key === 'Tab') {
        handleTabNavigation(e);
    }

    // Close modals with Escape
    if (e.key === 'Escape') {
        closeActiveModals();
    }
}

/**
 * Handle Tab key navigation within modals (focus trap)
 * @param {KeyboardEvent} e - Keyboard event
 */
function handleTabNavigation(e) {
    const activeModal = document.querySelector('.gallery-modal.active, #reviewSuccessMessage[style*="block"]');
    if (!activeModal) return;

    const focusableElements = getFocusableElements(activeModal);

    if (focusableElements.length > 0) {
        trapFocus(e, focusableElements);
    }
}

/**
 * Get all focusable elements within a container
 * @param {HTMLElement} container - Container element
 * @returns {NodeList} Focusable elements
 */
function getFocusableElements(container) {
    return container.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
}

/**
 * Trap focus within focusable elements
 * @param {KeyboardEvent} e - Keyboard event
 * @param {NodeList} focusableElements - Focusable elements
 */
function trapFocus(e, focusableElements) {
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
    } else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
    }
}

/**
 * Close all active modals
 */
function closeActiveModals() {
    closeGalleryModals();
    closeReviewSuccessModal();
}

/**
 * Close active gallery modals
 */
function closeGalleryModals() {
    const modals = document.querySelectorAll('.gallery-modal.active');
    modals.forEach(modal => {
        const closeBtn = modal.querySelector('.modal-close');
        if (closeBtn) {
            closeBtn.click();
        }
    });
}

/**
 * Close review success modal
 */
function closeReviewSuccessModal() {
    const successModal = document.getElementById('reviewSuccessMessage');
    if (successModal && successModal.style.display === 'block') {
        if (window.ReviewModule && window.ReviewModule.closeSuccessMessage) {
            window.ReviewModule.closeSuccessMessage();
        }
    }
}

/**
 * Add missing ARIA labels to interactive elements
 */
function addMissingAriaLabels() {
    addButtonLabels();
    addNavigationLabels();
}

/**
 * Add ARIA labels to buttons without text content
 */
function addButtonLabels() {
    const buttons = document.querySelectorAll('button:not([aria-label]):not(:has(span, strong))');
    buttons.forEach(btn => {
        if (!btn.textContent.trim()) {
            btn.setAttribute('aria-label', 'Button');
        }
    });
}

/**
 * Add ARIA labels to navigation links
 */
function addNavigationLabels() {
    const navLinks = document.querySelectorAll('.nav-link:not([aria-label])');
    navLinks.forEach(link => {
        const text = link.textContent.trim();
        if (text) {
            link.setAttribute('aria-label', `Navigate to ${text} page`);
        }
    });
}

/**
 * Check color contrast (development helper)
 * Note: This is a placeholder for integration with contrast checking tools
 */
function checkColorContrast() {
    if (typeof process !== 'undefined' && process?.env?.NODE_ENV === 'development') {
        // This would typically use a contrast checking library like axe-core
        console.log('✓ Color contrast check would run in development mode');
    }
}

/**
 * Skip to main content (for screen readers)
 * @param {string} mainContentId - ID of main content element
 */
export function skipToMainContent(mainContentId = 'main-content') {
    const mainContent = document.getElementById(mainContentId);
    if (mainContent) {
        mainContent.setAttribute('tabindex', '-1');
        mainContent.focus();
    }
}

/**
 * Announce to screen readers
 * @param {string} message - Message to announce
 * @param {string} priority - 'polite' or 'assertive' (default: 'polite')
 */
export function announceToScreenReaders(message, priority = 'polite') {
    const announcement = document.createElement('div');
    announcement.setAttribute('role', 'status');
    announcement.setAttribute('aria-live', priority);
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    announcement.textContent = message;

    document.body.appendChild(announcement);

    // Remove after announcement
    setTimeout(() => {
        announcement.remove();
    }, 1000);
}

/**
 * Cleanup accessibility event listeners
 */
export function cleanupAccessibility() {
    document.removeEventListener('keydown', handleKeyboardEvent);
}
